/**
 * Supabase Client Configuration for Frontend
 *
 * This file configures the Supabase client for browser-side use.
 * Falls back to localStorage-based token management when Supabase is not configured.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Check if Supabase is properly configured
const isSupabaseConfigured = () => !!(supabaseUrl &&
            supabaseAnonKey &&
            !supabaseUrl.includes('placeholder') &&
            !supabaseUrl.includes('localhost:54321') &&
            supabaseAnonKey !== 'placeholder');

const USE_SUPABASE = isSupabaseConfigured();

// Dynamic import and client creation
let supabase = null;
let supabasePromise = null;

const getSupabaseClient = async () => {
  if (!USE_SUPABASE) {
    return null;
  }

  if (supabase) {
    return supabase;
  }

  if (!supabasePromise) {
    supabasePromise = import('@supabase/supabase-js').then(({ createClient }) => {
      supabase = createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          storage: localStorage,
          storageKey: 'oval-auth-token',
          detectSessionInUrl: true
        }
      });
      return supabase;
    }).catch((err) => {
      console.warn('Failed to load Supabase client:', err);
      return null;
    });
  }

  return supabasePromise;
};

// Auth helpers - with fallback for non-Supabase mode
export const getSession = async () => {
  if (!USE_SUPABASE) {
    // Fallback: return null, let api.js handle localStorage tokens
    return null;
  }

  try {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data: { session }, error } = await client.auth.getSession();
    if (error) {
      console.error('Error getting session:', error);
      return null;
    }
    return session;
  } catch (err) {
    console.warn('Supabase session error:', err);
    return null;
  }
};

export const getAccessToken = async () => {
  if (!USE_SUPABASE) {
    // Fallback: return token from localStorage
    return localStorage.getItem('token') || null;
  }

  try {
    const session = await getSession();
    return session?.access_token || localStorage.getItem('token') || null;
  } catch {
    return localStorage.getItem('token') || null;
  }
};

export const refreshSession = async () => {
  if (!USE_SUPABASE) {
    // Fallback: return null, api.js will handle server-side refresh
    return null;
  }

  try {
    const client = await getSupabaseClient();
    if (!client) return null;

    const { data: { session }, error } = await client.auth.refreshSession();
    if (error) {
      console.error('Error refreshing session:', error);
      return null;
    }
    return session;
  } catch (err) {
    console.warn('Supabase refresh error:', err);
    return null;
  }
};

// OAuth helpers
export const signInWithProvider = async (provider) => {
  if (!USE_SUPABASE) {
    throw new Error('OAuth requires Supabase configuration. Please configure VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
  }

  const client = await getSupabaseClient();
  if (!client) {
    throw new Error('Supabase client not available');
  }

  const { data, error } = await client.auth.signInWithOAuth({
    provider,
    options: {
      redirectTo: `${window.location.origin}/auth/callback`
    }
  });

  if (error) {
    throw error;
  }

  return data;
};

// Listen for auth state changes
export const onAuthStateChange = (callback) => {
  if (!USE_SUPABASE) {
    // Fallback: return mock subscription that does nothing
    return {
      data: {
        subscription: {
          unsubscribe: () => {}
        }
      }
    };
  }

  // Return a promise that resolves to the subscription
  const subscriptionPromise = getSupabaseClient().then((client) => {
    if (!client) {
      return {
        data: {
          subscription: {
            unsubscribe: () => {}
          }
        }
      };
    }

    return client.auth.onAuthStateChange((event, session) => {
      callback(event, session);
    });
  });

  // Return object with unsubscribe that handles the async subscription
  return {
    data: {
      subscription: {
        unsubscribe: async () => {
          const result = await subscriptionPromise;
          result?.data?.subscription?.unsubscribe?.();
        }
      }
    }
  };
};

// Export configuration status for debugging
export const isConfigured = USE_SUPABASE;

// Export getter for supabase client (for advanced usage)
export { getSupabaseClient };

export default { getSession, getAccessToken, refreshSession, signInWithProvider, onAuthStateChange, isConfigured };
